import {
  Dialog,
  DialogActions,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { Close } from "@material-ui/icons";
import Button from "components/Forms/CustomButtons/ButtonTS";

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useActivityItemContext } from "store/activityItem";
import { Typography } from "@mui/material";
import { ActivityItem } from "store/activityItem/activityItemContext.types";
import { GenericInformationModal } from "../GenericInformationModal";

interface ConfirmReviewActivityModalProps {
  modalOpen: boolean;
  isUploading: boolean;
  onClose: () => void;
  onConfirm?: (activity: ActivityItem, reason: string) => void;
}

type Modal = {
  open?: boolean;
  modalTitle: string;
  modalDescription: string;
  modalType: "confirm-action" | "sensitive-action";
  onAction: () => void;
};

export default function ConfirmReviewActivityModal({
  modalOpen,
  isUploading,
  onClose,
  onConfirm,
}: ConfirmReviewActivityModalProps): JSX.Element {
  const { t } = useTranslation("manual-validation-modal");

  const { currentActivityItem } = useActivityItemContext((state) => ({
    currentActivityItem: state.currentActivity,
  }));

  const [reason, setReason] = useState<string>("");

  const [modals, setModals] = useState<Modal>({
    open: false,
    modalTitle: "",
    modalDescription: "",
    modalType: "confirm-action",
    onAction: null,
  });

  const closeModal = () => {
    setModals(() => ({
      open: false,
      modalTitle: "",
      modalDescription: "",
      modalType: "confirm-action",
      onAction: null,
    }));
  };

  const openModal = (data: Modal) => {
    setModals({
      open: true,
      ...data,
    });
  };

  const handleOnConfirm = () => {
    if (currentActivityItem && onConfirm) {
      openModal({
        modalTitle: t("ReView", {
          ns: "activity-item-table",
        }),
        modalDescription: t("confirm-review-activity", { ns: "modals" }),
        modalType: "confirm-action",
        onAction: () => {
          closeModal();
          onConfirm(currentActivityItem, reason);

          setReason("");
        },
      });
    }
  };

  const hasDisabledValidation = useMemo(() => {
    return isUploading || !reason;
  }, [isUploading, reason]);

  return (
    <>
      {modals.open && (
        <GenericInformationModal
          modalOpen={modals.open}
          modalTitle={modals.modalTitle}
          modalDescription={modals.modalDescription}
          modalType={modals.modalType}
          onClose={() => {
            closeModal();
          }}
          onAction={modals.onAction}
        />
      )}
      <Dialog
        open={modalOpen}
        fullWidth
        scroll="body"
        PaperProps={{
          style: {
            overflowY: "visible",
            width: "550px",
          },
        }}
        onClose={() => {
          onClose();
        }}
        disableBackdropClick
      >
        <DialogTitle>
          {t("ReView", {
            ns: "activity-item-table",
          })}
          <Button
            justIcon
            simple
            color="tumblr"
            round
            size="sm"
            style={{
              position: "absolute",
              right: "10px",
              top: "10px",
            }}
            onClick={onClose}
          >
            <Close />
          </Button>
        </DialogTitle>
        <Box display="flex" flexDirection="column" padding={"0 1rem 1rem 1rem"}>
          <Typography
            variant="overline"
            color="textSecondary"
            style={{ marginTop: "16px" }}
          >
            {t("review-reason")}
          </Typography>
          <TextField
            disabled={isUploading}
            variant="outlined"
            className="cy-reason-text-field"
            value={reason}
            multiline
            rows={4}
            onChange={(e) => setReason(e.target.value)}
          />
        </Box>

        <DialogActions>
          <Button
            disabled={isUploading}
            color="tumblr"
            simple
            onClick={onClose}
          >
            {t("cancel")}
          </Button>

          <Button
            disabled={hasDisabledValidation}
            color="primary"
            onClick={handleOnConfirm}
          >
            {t("send")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
